import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Sidebar from './Sidebar';
import Header from './Header';

interface MainLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  showBackButton?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ 
  children, 
  title, 
  subtitle, 
  headerActions,
  showBackButton = false 
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Fechar menu mobile ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Bloquear scroll do body quando menu mobile estiver aberto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay - com transição suave */}
      <div
        className={cn(
          'fixed inset-0 bg-foreground/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ease-in-out',
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        )}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* Mobile sidebar drawer - com animação deslizante */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 lg:hidden transform transition-transform duration-300 ease-in-out will-change-transform',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar isMobile onCloseMobile={closeMobileMenu} />
      </div>

      {/* Main content */}
      <main className={cn('transition-[margin-left] duration-300', 'ml-0 lg:ml-64')}>
        <Header
          title={title}
          subtitle={subtitle}
          headerActions={headerActions}
          onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        />
        
        {/* Botão voltar em mobile */}
        {showBackButton && (
          <div className="lg:hidden px-4 pt-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="min-h-[44px] min-w-[44px] -ml-2 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span>Voltar</span>
            </Button>
          </div>
        )}
        
        {/* Conteúdo com padding responsivo */}
        <div className="p-3 sm:p-4 md:p-6">{children}</div>
      </main>
    </div>
  );
};

export default MainLayout;
